import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IItineraryPlanEntityAttributes extends IModelAttributes {
	eventDetailId?: string;
	eventDetail?: Models.EventDetailEntity | Models.IEventDetailEntityAttributes;
	itineraryItems: Array<Models.ItineraryItemEntity | Models.IItineraryItemEntityAttributes>;
}

@entity('ItineraryPlanEntity', 'Itinerary Plan')
export default class ItineraryPlanEntity extends Model implements IItineraryPlanEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The ID of the event this itinerary plan belongs to.
	 */
	@observable
	@attribute()
	public eventDetailId?: string;

	/**
	 * The event this itinerary plan belongs to.
	 */
	@observable
	@attribute({ isReference: true })
	public eventDetail?: Models.EventDetailEntity | Models.IEventDetailEntityAttributes;

	/**
	 * The items in this itinerary plan.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public itineraryItems: Array<Models.ItineraryItemEntity | Models.IItineraryItemEntityAttributes> = [];

	constructor(attributes?: Partial<IItineraryPlanEntityAttributes>) {
		super(attributes);
		makeObservable(this);
		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * @param attributes The raw attributes to assign to this model.
	 */
	@action
	public assignAttributes(attributes?: Partial<IItineraryPlanEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.eventDetailId !== undefined) {
				this.eventDetailId = attributes.eventDetailId;
			}
			if (attributes.eventDetail !== undefined) {
				if (attributes.eventDetail === null) {
					this.eventDetail = attributes.eventDetail;
				} else if (attributes.eventDetail instanceof Models.EventDetailEntity) {
					this.eventDetail = attributes.eventDetail;
					this.eventDetailId = attributes.eventDetail.id;
				} else {
					this.eventDetail = new Models.EventDetailEntity(attributes.eventDetail);
					this.eventDetailId = this.eventDetail.id;
				}
			}
			if (attributes.itineraryItems !== undefined) {
				this.itineraryItems = attributes.itineraryItems.map(item => {
					if (item instanceof Models.ItineraryItemEntity) {
						return item;
					}
					return new Models.ItineraryItemEntity(item);
				});
			}
		}
	}
}
